import {
    MutableRefObject,
    PointerEvent as ReactPointerEvent,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { ClientPoint, HoveredRoi, RenderAdapter } from '../adapters/types';
import { POINTER_SHIELD_ATTRIBUTE } from '../chrome';

/** Nearest-ROI hit-testing runs per move; debounce so it doesn't run per pixel. */
const HOVER_DEBOUNCE_MS = 150;

/** An ROI under the pointer, and the client point the pick resolved at. */
interface HoveredRoiPick extends HoveredRoi, ClientPoint {}

export interface HoveredRoiState {
    /** The ROI under the cursor, or undefined when none / while pressed. */
    readonly pick: HoveredRoiPick | undefined;
    /**
     * Receives the label that shows the pick. While the pointer is over it,
     * the pick holds: reaching for its button takes the pointer off the
     * contour that raised it.
     */
    readonly labelRef: MutableRefObject<HTMLDivElement | null>;
    readonly onPointerMove: (event: ReactPointerEvent) => void;
    readonly onPointerDown: () => void;
    readonly onPointerUp: () => void;
    /**
     * Ends any press and drops the lookup still pending, and keeps the
     * pick: for the pointer leaving the view, where no release may follow.
     */
    readonly onPointerLeave: () => void;
    /**
     * Drops the lookup still pending, and keeps the pick and any press:
     * for the pointer leaving the canvas only for what lies over it, such
     * as the label or a host overlay's line, whether on its way to the
     * label's button or in the middle of a gesture.
     */
    readonly cancelPendingLookup: () => void;
    /**
     * Drops the pick and any pending lookup: for the pointer leaving the
     * view the label is shown in, for a wheel over the canvas, at once and
     * whatever the wheel goes on to change, and for a change of the view
     * the renderer's cursor source does not report, such as a resize. The
     * same function on every render.
     */
    readonly dropPick: () => void;
}

/** Whether a client point lies inside an element's on-screen box. */
function isOver(
    element: HTMLElement | null,
    clientX: number,
    clientY: number
): boolean {
    if (!element) {
        return false;
    }
    const rect = element.getBoundingClientRect();
    return (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
    );
}

/**
 * The pick after a lookup at a client point. The pick keeps the point it
 * first resolved at while the pointer stays on the same ROI (only a rename
 * shows), and holds whatever lies below while the point is over the label:
 * moving toward the label's button is itself a move, and a pick that
 * re-pinned on it would walk the button away from the hand reaching for it.
 */
function nextPick(
    previous: HoveredRoiPick | undefined,
    roi: HoveredRoi | undefined,
    clientX: number,
    clientY: number,
    overLabel: boolean
): HoveredRoiPick | undefined {
    if (previous && overLabel) {
        return previous;
    }
    if (!roi) {
        return undefined;
    }
    if (previous && previous.roiId === roi.roiId) {
        return previous.name === roi.name
            ? previous
            : { ...previous, name: roi.name };
    }
    return { ...roi, clientX, clientY };
}

/**
 * The visible ROI under the cursor, for a per-tile hover label. Reads the
 * tile's own structure set through the renderer — a passive readout, never
 * an edit, so it needs no edit-target resolution. Debounced so the engine
 * hit-test (nearest ROI over the whole structure set) does not run on every
 * pixel, and suppressed while the pointer is pressed so a brush/line stroke
 * never flashes ROI names. The pick is
 * pinned: see {@link nextPick}. Leaving the canvas keeps it, since what
 * lies over the canvas (the label, a host overlay) is still within the
 * view; leaving the view is the host's to report through
 * {@link HoveredRoiState.dropPick}.
 *
 * The pick is a client point, and it holds only while the view under it
 * holds: every change the renderer's cursor source reports (a slice
 * scroll, a zoom, a remount) drops it, as does {@link HoveredRoiState.dropPick}.
 * A kept pick would otherwise name, and offer to delete at, whatever the
 * same client point shows after the change.
 */
export function useHoveredRoi(renderer: RenderAdapter): HoveredRoiState {
    const [pick, setPick] = useState<HoveredRoiPick | undefined>(undefined);
    const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
        undefined
    );
    const pressedRef = useRef(false);
    const labelRef = useRef<HTMLDivElement | null>(null);

    const cancelPending = useCallback((): void => {
        if (timerRef.current !== undefined) {
            clearTimeout(timerRef.current);
            timerRef.current = undefined;
        }
    }, []);

    const dropPick = useCallback((): void => {
        cancelPending();
        setPick(undefined);
    }, [cancelPending]);

    useEffect(() => cancelPending, [cancelPending]);

    useEffect(
        () => renderer.getCursorSource().subscribe(dropPick),
        [renderer, dropPick]
    );

    const onPointerMove = (event: ReactPointerEvent): void => {
        if (pressedRef.current) {
            return;
        }
        if (
            event.target instanceof Element &&
            event.target.closest(`[${POINTER_SHIELD_ATTRIBUTE}]`)
        ) {
            dropPick();
            return;
        }
        const { clientX, clientY } = event;
        cancelPending();
        timerRef.current = setTimeout(() => {
            const roi = renderer.readRoiAt(clientX, clientY);
            const overLabel = isOver(labelRef.current, clientX, clientY);
            setPick((previous) =>
                nextPick(previous, roi, clientX, clientY, overLabel)
            );
        }, HOVER_DEBOUNCE_MS);
    };

    // A press starts a gesture (draw/pan); hide the name until the pointer is
    // moving freely again so a stroke never flashes names.
    const onPointerDown = (): void => {
        pressedRef.current = true;
        dropPick();
    };

    const onPointerUp = (): void => {
        pressedRef.current = false;
    };

    const onPointerLeave = (): void => {
        pressedRef.current = false;
        cancelPending();
    };

    return {
        pick,
        labelRef,
        onPointerMove,
        onPointerDown,
        onPointerUp,
        onPointerLeave,
        cancelPendingLookup: cancelPending,
        dropPick,
    };
}
